import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { deliveries as deliveriesApi, helpdesk as helpdeskApi } from "../services/api";
import {
  LayoutDashboard,
  Users,
  LayoutGrid,
  Layers,
  Home,
  DoorOpen,
  Package,
  CreditCard,
  BarChart3,
  FileText,
  Ticket,
  Calendar,
  Wrench,
  Boxes,
  BadgeCheck,
  ShieldCheck,
  Bot,
  Megaphone,
  Building2,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Shield,
  Sparkles,
  TrendingUp,
  ToggleRight,
  IndianRupee,
  Landmark,
  ArrowLeftRight,
  Car,
  Vote,
  UserCheck,
  ShieldOff,
  Receipt,
  BookOpen,
  PiggyBank,
  Coins,
  MessageCircle,
  Rss,
  UserCog,
  Video,
  ShoppingBag,
  AlertTriangle,
  Wallet,
} from "lucide-react";

interface NavItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  badge?: string | number;
  badgeType?: "red" | "blue" | "green";
  isAI?: boolean;
  residentVisible?: boolean;
  residentOnly?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: "Overview",
    items: [
      {
        icon: <LayoutDashboard size={16} />,
        label: "Dashboard",
        path: "/dashboard",
        residentVisible: true,
      },
    ],
  },
  {
    title: "Community",
    items: [
      {
        icon: <Layers size={16} />,
        label: "Blocks",
        path: "/dashboard/blocks",
      },
      {
        icon: <LayoutGrid size={16} />,
        label: "Units",
        path: "/dashboard/units",
      },
      {
        icon: <Home size={16} />,
        label: "Flat Types",
        path: "/dashboard/unit-types",
      },
      {
        icon: <Users size={16} />,
        label: "Residents",
        path: "/dashboard/residents",
        residentVisible: true,
      },
      {
        icon: <ArrowLeftRight size={16} />,
        label: "Move Requests",
        path: "/dashboard/move-requests",
        residentVisible: true,
      },
      {
        icon: <Car size={16} />,
        label: "Parking",
        path: "/dashboard/parking",
        residentVisible: true,
      },
      {
        icon: <UserCheck size={16} />,
        label: "Helpers",
        path: "/dashboard/helpers",
      },
      {
        icon: <ShieldOff size={16} />,
        label: "Blacklist",
        path: "/dashboard/blacklist",
      },
      {
        icon: <DoorOpen size={16} />,
        label: "Visitors",
        path: "/dashboard/visitors",
        residentVisible: true,
      },
      {
        icon: <Package size={16} />,
        label: "Deliveries",
        path: "/dashboard/deliveries",
        badgeType: "blue",
        residentVisible: true,
      },
      {
        icon: <Building2 size={16} />,
        label: "Facilities",
        path: "/dashboard/facilities",
      },
      {
        icon: <Calendar size={16} />,
        label: "Book a Facility",
        path: "/dashboard/facility-booking",
        residentVisible: true,
      },
      {
        icon: <Car size={16} />,
        label: "Vehicles",
        path: "/dashboard/vehicles",
        residentVisible: true,
      },
      {
        icon: <AlertTriangle size={16} />,
        label: "Violations",
        path: "/dashboard/violations",
        residentVisible: true,
      },
      {
        icon: <Video size={16} />,
        label: "Committee Meetings",
        path: "/dashboard/committee-meetings",
        residentVisible: true,
      },
      {
        icon: <MessageCircle size={16} />,
        label: "Messages",
        path: "/dashboard/messages",
        residentVisible: true,
        residentOnly: true,
      },
      {
        icon: <MessageCircle size={16} />,
        label: "Messages",
        path: "/dashboard/admin-messages",
      },
      {
        icon: <ShoppingBag size={16} />,
        label: "Marketplace",
        path: "/dashboard/marketplace",
        residentVisible: true,
      },
      {
        icon: <Wallet size={16} />,
        label: "Deposits",
        path: "/dashboard/deposits",
      },
    ],
  },
  {
    title: "Finance",
    items: [
      {
        icon: <CreditCard size={16} />,
        label: "Billing",
        path: "/dashboard/billing",
      },
      {
        icon: <CreditCard size={16} />,
        label: "My Billing",
        path: "/dashboard/my-billing",
        residentVisible: true,
        residentOnly: true,
      },
      {
        icon: <Receipt size={16} />,
        label: "Expenses",
        path: "/dashboard/expenses",
      },
      {
        icon: <BookOpen size={16} />,
        label: "Ledger",
        path: "/dashboard/ledger",
      },
      {
        icon: <PiggyBank size={16} />,
        label: "Budgets",
        path: "/dashboard/budgets",
      },
      {
        icon: <Coins size={16} />,
        label: "Funds",
        path: "/dashboard/funds",
      },
      {
        icon: <IndianRupee size={16} />,
        label: "Monthly Charges",
        path: "/dashboard/monthly-charges",
      },
      {
        icon: <BarChart3 size={16} />,
        label: "Accounting",
        path: "/dashboard/accounting",
      },
    ],
  },
  {
    title: "Analytics & Reporting",
    items: [
      {
        icon: <TrendingUp size={16} />,
        label: "Executive Dashboard",
        path: "/dashboard/analytics",
      },
      {
        icon: <FileText size={16} />,
        label: "Reports",
        path: "/dashboard/reports",
      },
      {
        icon: <ShieldCheck size={16} />,
        label: "Audit Logs",
        path: "/dashboard/audit-logs",
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        icon: <Ticket size={16} />,
        label: "Helpdesk",
        path: "/dashboard/helpdesk",
        badgeType: "red",
        residentVisible: true,
      },
      {
        icon: <Wrench size={16} />,
        label: "Maintenance",
        path: "/dashboard/maintenance",
      },
      {
        icon: <Boxes size={16} />,
        label: "Assets & Vendors",
        path: "/dashboard/assets",
      },
      {
        icon: <BadgeCheck size={16} />,
        label: "Staff & Payroll",
        path: "/dashboard/staff",
      },
      {
        icon: <Megaphone size={16} />,
        label: "Noticeboard",
        path: "/dashboard/notices",
        residentVisible: true,
      },
      {
        icon: <Vote size={16} />,
        label: "Polls",
        path: "/dashboard/polls",
        residentVisible: true,
      },
      {
        icon: <Calendar size={16} />,
        label: "Events",
        path: "/dashboard/events",
        residentVisible: true,
      },
      {
        icon: <Shield size={16} />,
        label: "Security",
        path: "/dashboard/security",
        residentVisible: true,
      },
      {
        icon: <FileText size={16} />,
        label: "Documents",
        path: "/dashboard/documents",
        residentVisible: true,
      },
      {
        icon: <Rss size={16} />,
        label: "Community Feed",
        path: "/dashboard/community-feed",
        residentVisible: true,
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        icon: <UserCog size={16} />,
        label: "Team Members",
        path: "/dashboard/team-members",
      },
      {
        icon: <ToggleRight size={16} />,
        label: "Community Features",
        path: "/dashboard/community-features",
      },
      {
        icon: <Landmark size={16} />,
        label: "Payment Config",
        path: "/dashboard/payment-config",
      },
    ],
  },
  {
    title: "Premium Themes",
    items: [
      {
        icon: <Sparkles size={16} />,
        label: "Executive Suite",
        path: "/dashboard/themes/executive-suite",
      },
      {
        icon: <Sparkles size={16} />,
        label: "Modern Minimal",
        path: "/dashboard/themes/modern-minimal",
      },
      {
        icon: <Sparkles size={16} />,
        label: "Corporate Edge",
        path: "/dashboard/themes/corporate-edge",
      },
    ],
  },
  {
    title: "AI Features",
    items: [
      {
        icon: <Bot size={16} />,
        label: "NexaBot",
        path: "/dashboard/nexabot",
        isAI: true,
      },
      {
        icon: <Sparkles size={16} />,
        label: "AI Insights",
        path: "/dashboard/insights",
        isAI: true,
      },
    ],
  },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const isResident = user?.role === 'RESIDENT' || user?.role === 'TENANT';
  const isActive = (path: string) => location.pathname === path;

  const [liveBadges, setLiveBadges] = useState<Record<string, number>>({});

  useEffect(() => {
    const sid = user?.societyId;
    if (!sid) return;

    const loadBadges = () => {
      deliveriesApi.list(sid)
        .then(list => setLiveBadges(b => ({
          ...b,
          '/dashboard/deliveries': list.filter(d => d.status === 'arrived' || d.status === 'notified').length,
        })))
        .catch(() => {});

      helpdeskApi.list(sid)
        .then(list => setLiveBadges(b => ({
          ...b,
          '/dashboard/helpdesk': list.filter(t => t.status === 'open' || t.status === 'in-progress').length,
        })))
        .catch(() => {});
    };

    // Badge counts can go stale the moment a visitor/delivery/ticket is actioned
    // elsewhere without a full page reload, so refresh on navigation and periodically.
    loadBadges();
    const interval = setInterval(loadBadges, 30_000);
    return () => clearInterval(interval);
  }, [user?.societyId, location.pathname]);

  const visibleSections = navSections
    .map(section => ({
      ...section,
      items: section.items
        .filter(item => (isResident ? item.residentVisible : !item.residentOnly))
        .map(item => {
          const live = liveBadges[item.path];
          return live !== undefined ? { ...item, badge: live > 0 ? live : undefined } : item;
        }),
    }))
    .filter(section => section.items.length > 0);

  return (
    <aside
      style={{
        position: "fixed",
        top: 60,
        left: 0,
        bottom: 0,
        width: collapsed ? 64 : 220,
        background: "var(--nx-sidebar-bg)",
        borderRight: "1px solid var(--nx-sidebar-border)",
        display: "flex",
        flexDirection: "column",
        transition: "width 300ms cubic-bezier(0.4,0,0.2,1)",
        zIndex: 90,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          position: "absolute",
          top: 16,
          right: -12,
          width: 24,
          height: 24,
          background: "var(--nx-sidebar-bg)",
          border: "1px solid var(--nx-sidebar-border)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 1,
          color: "var(--nx-sidebar-text)",
          transition: "all 200ms",
        }}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Nav content */}
      <nav style={{ flex: 1, padding: "16px 0 16px", overflowY: "auto" }}>
        {visibleSections.map((section) => (
          <div key={section.title} style={{ marginBottom: 4 }}>
            {/* Section label */}
            {!collapsed && (
              <div
                style={{
                  padding: "10px 16px 4px",
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "var(--nx-sidebar-text-muted)",
                  textTransform: "uppercase",
                  fontFamily: "Syne, sans-serif",
                }}
              >
                {section.title}
              </div>
            )}

            {/* Items */}
            {section.items.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={collapsed ? item.label : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: collapsed ? "10px 0" : "9px 16px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    margin: "1px 8px",
                    borderRadius: 8,
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: active ? 500 : 400,
                    color: active
                      ? "var(--nx-sidebar-active-text)"
                      : item.isAI
                        ? "rgba(96,165,250,0.7)"
                        : "var(--nx-sidebar-text)",
                    background: active
                      ? "var(--nx-sidebar-item-active-bg)"
                      : "transparent",
                    borderLeft:
                      active && !collapsed
                        ? "2.5px solid var(--nx-primary-accent)"
                        : "2.5px solid transparent",
                    transition: "all 180ms",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      (e.currentTarget as HTMLElement).style.background =
                        "var(--nx-sidebar-item-hover-bg)";
                      (e.currentTarget as HTMLElement).style.color =
                        "var(--nx-sidebar-item-hover-text)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      (e.currentTarget as HTMLElement).style.background =
                        "transparent";
                      (e.currentTarget as HTMLElement).style.color = item.isAI
                        ? "rgba(96,165,250,0.7)"
                        : "var(--nx-sidebar-text)";
                    }
                  }}
                >
                  {/* Icon */}
                  <span
                    style={{
                      flexShrink: 0,
                      color: active
                        ? "var(--nx-primary-accent)"
                        : item.isAI
                          ? "rgba(96,165,250,0.7)"
                          : "inherit",
                    }}
                  >
                    {item.icon}
                  </span>

                  {/* Label */}
                  {!collapsed && (
                    <span
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </span>
                  )}

                  {/* Badge */}
                  {!collapsed && item.badge !== undefined && (
                    <span
                      style={{
                        padding: "1px 6px",
                        borderRadius: 99,
                        fontSize: 10,
                        fontWeight: 600,
                        background:
                          item.badgeType === "red"
                            ? "rgba(239,68,68,0.2)"
                            : item.badgeType === "blue"
                              ? "rgba(26,108,246,0.2)"
                              : "rgba(16,185,129,0.2)",
                        color:
                          item.badgeType === "red"
                            ? "#FCA5A5"
                            : item.badgeType === "blue"
                              ? "#93C5FD"
                              : "#6EE7B7",
                      }}
                    >
                      {item.badge}
                    </span>
                  )}

                  {/* AI chip */}
                  {!collapsed && item.isAI && (
                    <span
                      style={{
                        padding: "1px 6px",
                        borderRadius: 99,
                        fontSize: 9,
                        fontWeight: 700,
                        background: "rgba(26,108,246,0.15)",
                        color: "#60A5FA",
                        letterSpacing: "0.04em",
                      }}
                    >
                      AI
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div
        style={{
          padding: collapsed ? "12px 8px" : "12px 16px",
          borderTop: "1px solid var(--nx-sidebar-border)",
        }}
      >
        {[
          { icon: <Settings size={14} />, label: "Settings", path: "/dashboard/settings" },
          { icon: <HelpCircle size={14} />, label: "Help & Support", path: "/dashboard/help" },
        ].map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.label}
                to={item.path}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 8px",
                  borderRadius: 7,
                  background: active ? "var(--nx-sidebar-item-active-bg)" : "transparent",
                  border: "none",
                  color: active ? "var(--nx-sidebar-active-text)" : "var(--nx-sidebar-text-muted)",
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 180ms",
                  fontFamily: "var(--font-body)",
                  textDecoration: "none",
                  boxSizing: "border-box",
                  justifyContent: collapsed ? "center" : "flex-start",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.background = "var(--nx-sidebar-item-hover-bg)";
                    (e.currentTarget as HTMLElement).style.color = "var(--nx-sidebar-item-hover-text)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.color = "var(--nx-sidebar-text-muted)";
                  }
                }}
                title={collapsed ? item.label : undefined}
              >
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
          {!collapsed && (
            <div
              style={{
                marginTop: 8,
                padding: "0 8px",
                fontSize: 10,
                color: "var(--nx-sidebar-text-muted)",
                opacity: 0.6,
              }}
            >
              © {new Date().getFullYear()} Pentagon Analytics
            </div>
          )}
        </div>
    </aside>
  );
};

export default Sidebar;
